import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import CloverPoint from 'types/models/CloverPoint';
import EditCloverPointConfigContext from 'contexts/EditCloverPointConfigContext';
import { getCloverPoints } from 'services/cloverServices';
import AppContext from 'contexts/AppContext';
import SearchBar from 'components/SearchBar';
import useDebounce from 'hooks/useDebounce';
import BooleanIcon from 'components/common/BooleanIcon';

export default (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const { selectedCloverPointConfig, setSelectedCloverPoint } = React.useContext(EditCloverPointConfigContext);
  const [paginationCloverPoints, setPaginationCloverPoints] = useState<PaginationData<CloverPoint> | null>(null);
  const [searchValue, setSearchValue] = useState('');

  useDebounce(() => {
    if (searchValue.trim()) {
      loadData(undefined, searchValue);
    } else {
      loadData();
    }
  }, [searchValue]);

  React.useEffect(() => {
    if (currentPageListNumber && selectedCloverPointConfig) {
      loadData(currentPageListNumber);
    } else {
      history.push('/private/admin/payments/clover-point');
    }
  }, [currentPageListNumber]);

  const loadData = React.useCallback(async (page?: number, searchTerm?: string) => {
    if (!selectedCloverPointConfig) return;
    const paginationData = await getCloverPoints(selectedCloverPointConfig.id, page, searchTerm);
    if (paginationData) setPaginationCloverPoints(paginationData);
  }, []);

  const renderEditButton = (cloverPoint: CloverPoint): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          setSelectedCloverPoint(cloverPoint);
          history.push('posnets/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: CloverPoint[]): JSX.Element => {
    return (
      <>
        {paginationData.map((cloverPoint: CloverPoint) => (
          <TableRow key={cloverPoint.id}>
            <TableCell component="th" scope="row">
              {cloverPoint.name || '-'}
            </TableCell>
            <TableCell component="th" scope="row">
              {cloverPoint.serial || '-'}
            </TableCell>
            <TableCell component="th" scope="row">
              {cloverPoint.model}
            </TableCell>
            <TableCell component="th" scope="row">
              {cloverPoint.description}
            </TableCell>
            <TableCell component="th" scope="row">
              <BooleanIcon value={cloverPoint.isAutoService} showText />
            </TableCell>
            <TableCell component="th" scope="row">
              <BooleanIcon value={cloverPoint.acceptsCash} showText />
            </TableCell>
            <TableCell>
              <BooleanIcon value={cloverPoint.isUsedOnLuxuryBox} showText />
            </TableCell>
            <TableCell component="th" scope="row">
              {cloverPoint.luxuryBox
                ? `${cloverPoint.luxuryBox.floor}-${cloverPoint.luxuryBox.identifierOnFloor}`
                : '-'}
            </TableCell>
            <TableCell>{renderEditButton(cloverPoint)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <div>
      <SearchBar
        label={t('search-clover-placeholder')}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onClear={() => setSearchValue('')}
      />

      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="secondary" onClick={() => history.go(-1)}>
          {t('go-back')}
        </Button>
      </div>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listCloverPoint-page-name')}</TableCell>
              <TableCell>{t('listCloverPoint-page-serial')}</TableCell>
              <TableCell>{t('listCloverPoint-page-model')}</TableCell>
              <TableCell>{t('listCloverPointConfigs-page-description')}</TableCell>
              <TableCell>{t('listCloverPointConfigs-page-is-auto-service')}</TableCell>
              <TableCell>{t('listCloverPointConfigs-page-accepts-cash')}</TableCell>
              <TableCell>{t('listCloverPointConfigs-page-is-used-on-luxury-box')}</TableCell>
              <TableCell>{t('listCloverPointConfigs-page-luxury-box')}</TableCell>
              <TableCell>{t('listCloverPointConfigs-page-edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationCloverPoints?.data && paginationCloverPoints?.data.length > 0 ? (
              renderContent(paginationCloverPoints.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listCloverPoints-page-noPoints')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationCloverPoints && (
        <TablePagination
          component="div"
          count={paginationCloverPoints.total}
          page={paginationCloverPoints.currentPage - 1}
          onPageChange={(_, page) => setCurrentPageListNumber(page + 1)}
          rowsPerPage={paginationCloverPoints.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </div>
  );
};
